import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Sparkles, Shield, Star, CircleDot, BookOpen } from "lucide-react";

const languages = [
  { label: "English", value: "english" },
  { label: "हिंदी", value: "hindi" },
  { label: "தமிழ்", value: "tamil" },
  { label: "తెలుగు", value: "telugu" },
  { label: "मराठी", value: "marathi" },
  { label: "ಕನ್ನಡ", value: "kannada" },
];

const steps = [
  {
    title: "What is Matka?",
    description: "Learn the basics of Matka betting, how markets open and close, and the game flow.",
    rate: "Win 9×",
    icon: BookOpen,
    badge: "Done",
    status: "done",
  },
  {
    title: "Single Digit",
    description: "Choose a single number and place a bet for small odds and quick wins.",
    rate: "Win 9×",
    icon: CheckCircle2,
    badge: "Done",
    status: "done",
  },
  {
    title: "Jodi Bet",
    description: "Pick a pair of digits to win higher payout with better probability.",
    rate: "Win 90×",
    icon: Sparkles,
    badge: "Done",
    status: "done",
  },
  {
    title: "Patti Bet",
    description: "Play a three-digit combination for bigger payout and stronger rewards.",
    rate: "Win up to 700×",
    icon: Shield,
    badge: "Start",
    status: "active",
  },
];

function HowToPlay() {
  const navigate = useNavigate();
  const [selectedLanguage, setSelectedLanguage] = useState("english");
  const [activeStep, setActiveStep] = useState(0);
  const [showLessons, setShowLessons] = useState(false);

  const currentLanguage = useMemo(
    () => languages.find((lang) => lang.value === selectedLanguage) || languages[0],
    [selectedLanguage]
  );

  return (
    <div className="min-h-screen bg-slate-100 pb-24">
      <div className="mx-3 mt-3 rounded-[32px] bg-white p-4 shadow-lg shadow-slate-200/80">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
        >
          <ArrowLeft size={16} /> Back
        </button>
      </div>

      <div className="mx-3 mt-4 rounded-[32px] bg-white p-5 shadow-lg shadow-slate-200/70">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-[28px] bg-slate-100 text-sky-600 shadow-sm">
            <BookOpen size={32} />
          </div>
          <h1 className="text-xl font-bold">अपनी भाषा चुनें</h1>
          <p className="mt-2 text-sm text-slate-500">Matka सीखें</p>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {languages.map((lang) => (
            <button
              key={lang.value}
              type="button"
              onClick={() => setSelectedLanguage(lang.value)}
              className={`rounded-3xl border p-4 text-left shadow-sm transition hover:border-sky-400 ${
                selectedLanguage === lang.value
                  ? "border-sky-500 bg-sky-50 text-slate-900"
                  : "border-slate-200 bg-white text-slate-700"
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold">{lang.label}</div>
                  <div className="mt-1 text-xs text-slate-400">{lang.label}</div>
                </div>
                {selectedLanguage === lang.value ? (
                  <span className="grid h-8 w-8 place-items-center rounded-full bg-sky-600 text-white">
                    <CircleDot size={16} />
                  </span>
                ) : null}
              </div>
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => setShowLessons(true)}
          className="mt-6 w-full rounded-3xl bg-sky-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-sky-500/20 hover:bg-sky-700"
        >
          जारी रखें
        </button>
      </div>

      {showLessons && (
        <div className="mx-3 mt-4 rounded-[32px] bg-white p-5 shadow-lg shadow-slate-200/70">
          <div className="mb-5 rounded-3xl bg-sky-600 p-4 text-white">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.24em] opacity-80">Learn Matka</div>
                <div className="mt-1 text-sm opacity-90">{currentLanguage.label} · {activeStep + 1}/4 Done</div>
              </div>
              <div className="rounded-2xl bg-white/15 px-3 py-2 text-sm">{currentLanguage.label}</div>
            </div>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/20">
              <div className="h-full rounded-full bg-white" style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }} />
            </div>
          </div>

          <div className="space-y-3">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const active = index === activeStep;
              return (
                <button
                  key={step.title}
                  type="button"
                  onClick={() => setActiveStep(index)}
                  className={`w-full rounded-3xl border p-4 text-left shadow-sm transition ${
                    active ? "border-sky-500 bg-sky-50" : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="mt-1 rounded-2xl bg-slate-100 p-3 text-sky-600">
                      <Icon size={20} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <h2 className="font-semibold">{step.title}</h2>
                        <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
                          step.status === "done"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-slate-100 text-slate-700"
                        }`}>
                          {step.badge}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-slate-500">{step.description}</p>
                      <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                        <Star size={14} />
                        {step.rate}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-slate-500">Step {activeStep + 1}</div>
                <h3 className="mt-1 text-lg font-semibold">{steps[activeStep].title}</h3>
              </div>
              <span className="rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-700">
                {steps[activeStep].rate}
              </span>
            </div>
            <p className="text-sm leading-6 text-slate-600">{steps[activeStep].description}</p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <div className="rounded-3xl bg-white p-4 shadow-sm">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Bet type</div>
                <div className="mt-2 font-semibold">{steps[activeStep].title}</div>
              </div>
              <div className="rounded-3xl bg-white p-4 shadow-sm">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Win Rate</div>
                <div className="mt-2 font-semibold">{steps[activeStep].rate}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HowToPlay;
